import * as Sentry from "@sentry/react";
import { configureSentryAxiosInterceptor } from "../utils/sentryInterceptor";

/**
 * Get Sentry configuration based on environment
 */
const getSentryConfig = () => {
  const sentryDSN =
    "https://0955e2ea90b94062a1ee2a510e9cb4ce@o912544.ingest.sentry.io/5950458";
  const env = process.env.REACT_APP_ENV;
  const apiUrl = process.env.REACT_APP_API_BASE_URL || window.location.origin;
  let sampleRate;
  if (env === "production") {
    sampleRate = 1.0;
  } else if (env === "testing") {
    sampleRate = 0;
  } else {
    sampleRate = 0.1;
  }
  return {
    dsn: sentryDSN,
    environment: env || "local",
    tracesSampleRate: sampleRate,
    integrations: [
      Sentry.browserTracingIntegration({
        tracingOrigins: [window.location.origin, apiUrl, /^\//],
        shouldCreateSpanForRequest: (url) => {
          // Don't create spans for static assets
          return (
            !url.includes("/static/") &&
            !url.includes(".js") &&
            !url.includes(".css")
          );
        },
      }),
      // Only enable replay in development for testing
      ...(env === "development"
        ? [
            Sentry.replayIntegration({
              maskAllText: false,
              blockAllMedia: false,
            }),
          ]
        : []),
    ],
    // Replay settings for development
    ...(env === "development" && {
      replaysSessionSampleRate: 1.0,
      replaysOnErrorSampleRate: 1.0,
    }),
    beforeSend(event) {
      // Filter out noise from development
      if (event.message?.includes("Non-Error promise rejection captured")) {
        return null;
      }
      return event;
    },
  };
};

/**
 * Initialize Sentry with environment-based configuration
 * @param {Object} apiClient - Axios instance to configure with Sentry interceptors
 */
export const initializeSentry = (apiClient = null) => {
  try {
    const config = getSentryConfig();

    // Only initialize if DSN is provided
    if (!config.dsn) {
      console.log("Sentry DSN not provided, skipping initialization");
      return;
    }

    Sentry.init(config);
    console.log("✅ Sentry initialized successfully");

    // Configure axios interceptors after Sentry is initialized
    if (apiClient) {
      configureSentryAxiosInterceptor(apiClient);
      console.log("✅ Sentry axios interceptors configured");
    }
  } catch (error) {
    console.warn("Failed to initialize Sentry:", error);
    // Continue without Sentry rather than breaking the app
  }
};

const sentryConfig = { initializeSentry };

export default sentryConfig;
